import { Injectable } from '@angular/core';
import { ChannelHistory, ChannelHistoryEntry } from '@models/channel-history';
import { CircularBuffer } from 'ops/common/circular-buffer';
import { Subject, Observable } from 'rxjs';
import { ChannelHistoryValuesService } from './channel-history-values.service';

export interface ChannelHistoryResponse {
    histories: {
        fcuChannelId: number;
        log: any[];
        lastUpdated: number;
    }[];
    updateInterval: number;
    currentTime: number;
}

@Injectable()
export class ChannelHistoryService {
    private HISTORY_ENTRIES = 2000;

    public updateLookback: number = 3;

    private watchedChannels: number[] = [];
    private fullReloadChannelIds = new Set<number>();
    private log: ChannelHistory;

    private onHistoryUpdated$: Subject<ChannelHistory> = new Subject();

    private historyLookup: { [channelId: number]: ChannelHistoryEntry } = {};

    constructor(private channelHistoryValues: ChannelHistoryValuesService) {
        this.log = {
            histories: [],
            updateInterval: 0,
            currentTime: 0,
        };
    }

    public getGraphHistories() {
        return this.historyLookup;
    }

    public onHistoryUpdated(): Observable<ChannelHistory> {
        return this.onHistoryUpdated$;
    }

    public setWatchedChannels(channels: number[]) {
        const currentChannelIds = new Set<number>(this.watchedChannels);
        const nextChannelIds = new Set<number>(channels);
        const newlyAddedChannelIds = channels.filter((v) => !currentChannelIds.has(v));
        const removedChannelIds = this.watchedChannels.filter((v) => !nextChannelIds.has(v));

        for (let i = 0; i < removedChannelIds.length; i++) {
            this.fullReloadChannelIds.delete(removedChannelIds[i]);
            delete this.historyLookup[removedChannelIds[i]];
        }

        this.log.histories = this.log.histories.filter((v) => nextChannelIds.has(v.fcuChannelId));
        this.watchedChannels = [...channels];
        this.fullReloadChannelIds = new Set([
            ...this.fullReloadChannelIds,
            ...newlyAddedChannelIds,
        ]);
    }

    public async refresh(): Promise<void> {
        const appliedLookback = this.updateLookback; //Save in case this changes during the function execution

        const [fullUpdates, partialUpdates]: ChannelHistoryResponse[] = [
            this.loadFullHistories(),
            this.loadPartialHistories(appliedLookback),
        ];

        this.log.updateInterval = partialUpdates.updateInterval;
        this.log.currentTime = partialUpdates.currentTime;

        for (let i = 0; i < fullUpdates.histories.length; i++) {
            const receivedHistory = fullUpdates.histories[i];

            let newHistory: ChannelHistoryEntry = null;

            //Case: History is already in history list, but being reloaded.
            if (receivedHistory.fcuChannelId in this.historyLookup) {
                const updateChannel = this.historyLookup[receivedHistory.fcuChannelId];
                updateChannel.lastUpdated = receivedHistory.lastUpdated;
                updateChannel.log = new CircularBuffer(this.HISTORY_ENTRIES);
                newHistory = updateChannel;
            } else {
                newHistory = {
                    fcuChannelId: receivedHistory.fcuChannelId,
                    log: new CircularBuffer(this.HISTORY_ENTRIES),
                    lastUpdated: receivedHistory.lastUpdated,
                };

                this.log.histories.push(newHistory);
                this.historyLookup[newHistory.fcuChannelId] = newHistory;
            }

            for (let j = 0; j < receivedHistory.log.length; j++) {
                newHistory.log.appendValue(receivedHistory.log[j]);
            }
        }

        this.clearFullReloadQueue();

        for (let i = 0; i < partialUpdates.histories.length; i++) {
            const newRecords = partialUpdates.histories[i];
            const recordedHistory = this.historyLookup[newRecords.fcuChannelId];

            const timeDifference = newRecords.lastUpdated - recordedHistory.lastUpdated;

            const expectedNewEntries = Math.floor(timeDifference / partialUpdates.updateInterval);

            if (expectedNewEntries > appliedLookback) {
                console.warn(
                    `WARNING: Channel out of sync: ${newRecords.fcuChannelId}. Reloading next update. Distance: ${expectedNewEntries}`,
                );
                this.enqueueFullReload(newRecords.fcuChannelId);
                continue;
            }

            recordedHistory.lastUpdated = newRecords.lastUpdated;

            for (
                let i = newRecords.log.length - expectedNewEntries;
                i < newRecords.log.length;
                i++
            ) {
                recordedHistory.log.appendValue(newRecords.log[i]);
            }
        }

        this.onHistoryUpdated$.next(this.log);
    }

    private loadFullHistories(): ChannelHistoryResponse {
        return this.fullReloadChannelIds.size
            ? this.channelHistoryValues.getHistory([...this.fullReloadChannelIds])
            : { histories: [], updateInterval: 0, currentTime: 0 };
    }

    private loadPartialHistories(lookback: number): ChannelHistoryResponse {
        const requestChannels = this.watchedChannels.filter(
            (c) => !this.fullReloadChannelIds.has(c),
        );
        return this.channelHistoryValues.getHistory(requestChannels, lookback);
    }

    private enqueueFullReload(channelName) {
        this.fullReloadChannelIds.add(channelName);
    }

    private clearFullReloadQueue() {
        this.fullReloadChannelIds.clear();
    }

    public Cleanup() {
        this.onHistoryUpdated$.complete();
    }
}
